import { ButtonSize } from '@nx-frontend/shared/ui/button';
import { Org, OrgInfoSubscriptionType, PaywallPopupReason, PopupType, User } from '@nx-frontend/shared/types';
import { OpenModalFn } from '@nx-frontend/shared/types';
import { GetOpenPayWallPopupProps } from '@nx-frontend/shared/types';
import { convertBytes } from '@nx-frontend/shared/utils';

interface ShowAttachmentSizeLimitPopupParams {
  openModal: OpenModalFn;
  subscriptionType: OrgInfoSubscriptionType;
  currentOrganization: Org;
  currentUser: User;
  maxAttachmentSize: number;
  attachmentSize: number;
  attachmentFileName: string;
  getOpenPaywallPopup: (props: GetOpenPayWallPopupProps) => void;
}

/**
 * Shows paywall popup when attachment size limit is reached
 * @param params
 */
export function showAttachmentSizeLimitPopup(params: ShowAttachmentSizeLimitPopupParams) {
  const { openModal, currentOrganization, subscriptionType, currentUser, getOpenPaywallPopup } = params;

  const maxSize = convertBytes(params.maxAttachmentSize);

  if (currentOrganization.userId !== currentUser.id) {
    openModal({
      title: 'This file is too big to upload',
      content: PopupType.DIALOG,
      closeButtonSize: 18,
      payload: {
        payloadType: PopupType.DIALOG,
        text: (
          <div>
            You're trying to upload file larger than <strong>{maxSize}</strong>, which is the maximum size allowed for
            the organization:
            <ul className="list-disc mt-4 mb-4 ml-4">
              <li>
                {params.attachmentFileName} <strong>{convertBytes(params.attachmentSize)}</strong>
              </li>
            </ul>
            Contact the organization owner for more details.
          </div>
        ),
        buttons: [
          {
            main: true,
            size: ButtonSize.MD,
            text: 'Ok',
          },
        ],
      },
    });
    return;
  }

  if (subscriptionType !== OrgInfoSubscriptionType.STANDARD) {
    openModal({
      title: 'This file is too big to upload',
      content: PopupType.DIALOG,
      closeButtonSize: 18,
      payload: {
        payloadType: PopupType.DIALOG,
        text: (
          <div>
            You're trying to upload file larger than <strong>{maxSize}</strong>, which is the maximum size allowed for
            your organization:
            <ul className="list-disc mt-4 mb-4 ml-4">
              <li>
                {params.attachmentFileName} <strong>{convertBytes(params.attachmentSize)}</strong>
              </li>
            </ul>
            If you need to increase your limits please contact our{' '}
            <a
              rel="noreferrer"
              target="_blank"
              href="https://nimbusweb.me/contact-us.php"
              className="text-cyan-900 underline"
            >
              support team
            </a>
            .
          </div>
        ),
        buttons: [
          {
            main: true,
            size: ButtonSize.MD,
            text: 'Ok',
          },
        ],
      },
    });
    return;
  }

  getOpenPaywallPopup({
    openModal,
    payload: {
      payloadType: PopupType.PAYWALL,
      mode: 'upgrade',
      title: 'Allow yourself more: store all important files in one place with our advanced subscriptions!',
      reason: PaywallPopupReason.FILE_SIZE_LIMIT,
      orgId: currentOrganization.globalId,
      subscriptionType,
    },
  });
}
